import { useEffect, useMemo, useState } from "react";
import {
  ApiError,
  api,
  type BandejaItem,
  type CasoDetalle,
  type CasoImeiCandidato,
  type CorreoOut,
  type ResultadoJalar,
  type DatosCasa,
  type TriageIA,
} from "@/lib/api";
import { useAuth } from "@/lib/auth";
import AsignarCasoModal from "@/components/AsignarCasoModal";
import EmailHtmlPreview from "@/components/EmailHtmlPreview";
import NuevoCasoModal, {
  type NuevoCasoPrefill,
} from "@/components/NuevoCasoModal";
import ConfirmDialog from "@/ui/ConfirmDialog";
import AyudaVista from "@/ui/AyudaVista";
import { ayudaBandeja } from "@/components/ayuda/vistas/bandeja";
import { useToast } from "@/ui/Toast";
import { mostrarMsgId } from "@/lib/formato";
import RevisarCorreoModal from "@/components/buzon/RevisarCorreoModal";
import type { EstadoBuzon } from "@/lib/api";
import { useSearchParams } from "react-router-dom";

/**
 * Bandeja compartida: correos inbound (de SendGrid Inbound Parse) que NO
 * matchearon TE en su subject o que el user reenvio sin TE. Visible para
 * admin y soporte. Acciones: Asignar a caso, Descartar (admin only),
 * Crear caso desde correo (form parseado o sugerencia de IA), y triage con IA.
 */

/**
 * Gmail/Outlook al reenviar en texto plano duplican los links de email
 * como `addr@dom.com<mailto:addr@dom.com>`. Lo limpiamos para que el
 * prefill pase la validacion EmailStr del backend.
 */
function limpiarEmail(raw: string | undefined): string | undefined {
  if (!raw) return undefined;
  const m = raw.match(/[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/);
  return m ? m[0] : raw;
}

/**
 * Convierte metadata_parseada de un correo (Garantias u otro form
 * estructurado) a NuevoCasoPrefill. El cliente_id se queda undefined
 * para que el user lo elija con el ClientePicker.
 */
/**
 * El mismo mapeo, pero desde el `prefill` crudo que devuelve `POST
 * /api/buzon/jalar`. Es el MISMO diccionario que `metadata_parseada` (los dos
 * salen del parser de formularios de garantía), así que se reusa la conversión
 * en vez de escribir una segunda que se desincronice.
 */
function prefillDesdeBuzon(m: Record<string, unknown> | null | undefined): NuevoCasoPrefill {
  return metadataToPrefill({ metadata_parseada: m ?? {} } as unknown as BandejaItem);
}

function metadataToPrefill(item: BandejaItem): NuevoCasoPrefill {
  const m = item.metadata_parseada || {};
  return {
    marca: m.marca || undefined,
    modelo: m.modelo || undefined,
    imeis_raw: m.imei || undefined,
    contacto_nombre: m.contacto_nombre || undefined,
    contacto_email: limpiarEmail(m.contacto_email),
    contacto_telefono: m.contacto_telefono || undefined,
    descripcion_inicial: m.descripcion || undefined,
    // El form de Garantias siempre representa una falla del equipo
    tipo_categoria: m._form === "garantia" ? "falla" : undefined,
  };
}

/**
 * Convierte la decisión de la IA (triage_ia) a NuevoCasoPrefill. La IA leyó
 * el correo y extrajo marca/modelo/IMEIs/cliente/descripción/categoría; el
 * humano confirma antes de crear. cliente_id solo va si la IA lo resolvió.
 */
function triageToPrefill(t: TriageIA): NuevoCasoPrefill {
  return {
    cliente_id: t.cliente_id ?? undefined,
    cliente_nombre: t.cliente_nombre ?? undefined,
    marca: t.marca ?? undefined,
    modelo: t.modelo ?? undefined,
    imeis_raw: t.imeis.length ? t.imeis.join(", ") : undefined,
    contacto_nombre: t.contacto_nombre ?? undefined,
    contacto_email: limpiarEmail(t.contacto_email ?? undefined),
    contacto_telefono: t.contacto_telefono ?? undefined,
    descripcion_inicial: t.descripcion ?? undefined,
    tipo_categoria: t.categoria || undefined,
  };
}

function confianzaClase(c: string): string {
  if (c === "alta") return "border-emerald-500/40 bg-emerald-500/10 text-emerald-300";
  if (c === "media") return "border-amber-500/40 bg-amber-500/10 text-amber-300";
  return "border-zinc-500/40 bg-zinc-500/10 text-zinc-400";
}

function decisionTexto(t: TriageIA): string {
  if (t.decision === "nuevo") return "Caso nuevo";
  if (t.decision === "vincular")
    return t.caso_te ? `Pertenece a ${t.caso_te}` : "Pertenece a un caso";
  return "Sin acción";
}

export default function Bandeja() {
  const me = useAuth((s) => s.user);
  const toast = useToast();
  // Deep-link: el aviso del radar apunta a /bandeja?revisar=1 y abre el modal.
  const [searchParams, setSearchParams] = useSearchParams();
  const [revisarAbierto, setRevisarAbierto] = useState(searchParams.get("revisar") === "1");
  const [estadoBuzon, setEstadoBuzon] = useState<EstadoBuzon | null>(null);
  // Caso nuevo nacido del buzón: los correos YA se trajeron (existen en la base
  // sin caso), así que al crear el caso hay que pegárselos. Se guardan sus ids
  // y con qué pedirlos otra vez al buzón (ver `onCreated`).
  const [buzonNuevo, setBuzonNuevo] = useState<{
    prefill: NuevoCasoPrefill;
    correoIds: number[];
    mensajes: { uid: number; message_id: string | null }[];
    uidvalidity: number | null;
  } | null>(null);
  const [items, setItems] = useState<BandejaItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState<Record<number, CorreoOut | "loading">>({});
  const [asignarFor, setAsignarFor] = useState<BandejaItem | null>(null);
  const [descartarFor, setDescartarFor] = useState<BandejaItem | null>(null);
  const [crearCasoFor, setCrearCasoFor] = useState<BandejaItem | null>(null);
  // true = el prefill del modal sale de la IA; false = del form parseado.
  const [crearConIA, setCrearConIA] = useState(false);
  // Switch global del triage con IA (Config → Automatizaciones → Revisión con IA). Gatea los botones de IA.
  const [triageEnabled, setTriageEnabled] = useState(false);
  // Ver lo descartado. Descartar era de un clic y sin vuelta: el correo salía de
  // la bandeja, de todo caso y —si no estaba en el buzón personal— de cualquier
  // pantalla. Joe descartó uno por error y se quedó sin dónde buscarlo.
  const [verDescartados, setVerDescartados] = useState(false);
  // Borrado REAL (solo desde la vista de descartados). Es el único de la app.
  const [borrarFor, setBorrarFor] = useState<BandejaItem | null>(null);

  async function load() {
    setLoading(true);
    try {
      // Cache-bust con timestamp en query — algunos browsers cachean GETs.
      const list = await api.get<BandejaItem[]>(
        `/api/correos/bandeja?descartados=${verDescartados ? 1 : 0}&_t=${Date.now()}`,
      );
      setItems(list);
      window.dispatchEvent(new Event("bandeja:changed"));
    } catch (err) {
      const msg = err instanceof ApiError ? err.detail : "Error";
      toast.error("No pude cargar bandeja", msg);
    } finally {
      setLoading(false);
    }
  }

  // Estado del buzón: alimenta la línea «en tu buzón hay N que podrían servir».
  // Es el MISMO endpoint que usa Perfil; no se agrega otra llamada al ciclo.
  useEffect(() => {
    void api
      .get<EstadoBuzon>("/api/buzon/estado")
      .then(setEstadoBuzon)
      .catch(() => setEstadoBuzon(null));
  }, []);

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [verDescartados]);

  useEffect(() => {
    let vivo = true;
    api
      .get<DatosCasa>("/api/auth/soporte")
      .then((s) => {
        if (vivo) setTriageEnabled(s.triage_ia_activo);
      })
      .catch(() => {});
    return () => {
      vivo = false;
    };
  }, []);

  async function toggleExpand(item: BandejaItem) {
    if (expanded[item.id]) {
      const copy = { ...expanded };
      delete copy[item.id];
      setExpanded(copy);
      return;
    }
    setExpanded({ ...expanded, [item.id]: "loading" });
    try {
      const full = await api.get<CorreoOut>(`/api/correos/${item.id}`);
      setExpanded((prev) => ({ ...prev, [item.id]: full }));
    } catch (err) {
      const msg = err instanceof ApiError ? err.detail : "Error";
      toast.error("No pude cargar correo", msg);
      const copy = { ...expanded };
      delete copy[item.id];
      setExpanded(copy);
    }
  }

  async function handleDescartar() {
    if (!descartarFor) return;
    try {
      await api.del(`/api/correos/${descartarFor.id}`);
      toast.success("Correo descartado");
      setDescartarFor(null);
      void load();
    } catch (err) {
      const msg = err instanceof ApiError ? err.detail : "Error";
      toast.error("No pude descartar", msg);
    }
  }

  async function handleRecuperar(item: BandejaItem) {
    try {
      await api.post(`/api/correos/${item.id}/recuperar`);
      toast.success("De vuelta en la bandeja", "Ya puedes asignarlo a un caso.");
      void load();
    } catch (err) {
      const msg = err instanceof ApiError ? err.detail : "Error";
      toast.error("No pude recuperarlo", msg);
    }
  }

  async function handleBorrarDefinitivo() {
    if (!borrarFor) return;
    try {
      await api.del(`/api/correos/${borrarFor.id}/definitivo`);
      toast.success("Borrado", "Ya no está en la base.");
      setBorrarFor(null);
      void load();
    } catch (err) {
      const msg = err instanceof ApiError ? err.detail : "Error";
      toast.error("No pude borrarlo", msg);
    }
  }

  // Asignar el correo a un caso candidato (chip de match por IMEI).
  async function asignarDesdeChip(item: BandejaItem, cand: CasoImeiCandidato) {
    try {
      await api.post(`/api/correos/${item.id}/asignar`, { caso_id: cand.caso_id });
      toast.success("Correo asignado", `${cand.folio} — ${cand.cliente_nombre}`);
      void load();
    } catch (err) {
      const msg = err instanceof ApiError ? err.detail : "Error";
      toast.error("No pude asignar", msg);
    }
  }

  // Asignar el correo al caso que sugirió la IA (decision=vincular).
  async function asignarIA(item: BandejaItem, casoId: number) {
    try {
      await api.post(`/api/correos/${item.id}/asignar`, { caso_id: casoId });
      toast.success("Correo asignado", item.triage_ia?.caso_te || "");
      void load();
    } catch (err) {
      const msg = err instanceof ApiError ? err.detail : "Error";
      toast.error("No pude asignar", msg);
    }
  }

  // Pedir a la IA que analice el correo (corre Opus, persiste y, si está
  // muy segura + corroborado, auto-vincula). Refresca la bandeja al terminar.
  async function analizarConIA(item: BandejaItem) {
    try {
      await api.post(`/api/correos/${item.id}/triage-ia`);
      toast.success("Correo revisado con IA");
      void load();
    } catch (err) {
      const msg = err instanceof ApiError ? err.detail : "Error";
      toast.error("No se pudo revisar con IA", msg);
    }
  }

  const isAdmin = me?.rol === "admin";
  // El borrado real es del dueño, no de cualquier admin ("la mera punta del tren").
  const esDueno = me?.es_dueno === true;

  return (
    <div className="space-y-4">
      {/* En móvil el encabezado va en COLUMNA: título arriba, acciones en su
          propia fila. Apretados en una sola línea, el botón «Revisar mi correo»
          se partía en tres renglones y el título en dos — reportado por Joe
          desde su teléfono (~412 px), que es un ancho que no había verificado:
          se comprobó el modal a 390 px, pero no ESTA página después de meterle
          el botón. */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex min-w-0 items-start gap-2">
          <div className="min-w-0">
            <h1 className="text-xl font-semibold">Bandeja de correos</h1>
            <p className="text-sm text-zinc-400">
              {items.length}{" "}
              {verDescartados
                ? items.length === 1
                  ? "correo descartado"
                  : "correos descartados"
                : items.length === 1
                  ? "correo sin asignar a caso"
                  : "correos sin asignar a caso"}
            </p>
            {/* Qué ES esta lista, a la vista. Vivía solo dentro del «?» y del
                estado vacío, así que con correos enfrente nadie sabía de dónde
                salieron: Joe los buscó en su buzón personal, donde nunca
                estuvieron. Llegan a otra dirección. */}
            {items.length > 0 && !verDescartados && (
              <p className="text-xs text-zinc-500">
                Llegaron a <code className="text-zinc-400">casos@arknex.net</code>, no a tu
                buzón. Ábrelos para asignarlos a un caso o descartarlos.
              </p>
            )}
            {verDescartados && (
              <p className="text-xs text-zinc-500">
                Correos que se descartaron. <strong>No se borran</strong>: puedes devolverlos
                a la bandeja cuando quieras.
              </p>
            )}
            {/* La salida de emergencia del descarte. Sin esto, un clic
                equivocado dejaba el correo fuera de la bandeja, fuera de todo
                caso y sin pantalla donde buscarlo. */}
            <button
              type="button"
              onClick={() => setVerDescartados((v) => !v)}
              className="mt-0.5 block text-left text-xs text-zinc-500 underline-offset-2 hover:text-zinc-300 hover:underline"
            >
              {verDescartados ? "← Volver a los pendientes" : "Ver los descartados"}
            </button>
            {/* Solo se pinta cuando hay algo. Un radar callado es un radar
                funcionando: una línea que dice «0» todos los días se vuelve
                invisible y de paso hace invisible al día que sí importa. */}
            {estadoBuzon?.puede_servir ? (
              <button
                type="button"
                onClick={() => setRevisarAbierto(true)}
                className="mt-0.5 block text-left text-sm text-accent-400 hover:underline"
              >
                En tu buzón hay {estadoBuzon.puede_servir} que{" "}
                {estadoBuzon.puede_servir === 1 ? "podría" : "podrían"} servir.
              </button>
            ) : null}
          </div>
          <AyudaVista
            titulo="Bandeja de correos"
            {...ayudaBandeja({ isAdmin, esDueno, triageActivo: triageEnabled })}
          />
        </div>
        {/* `whitespace-nowrap` en los dos: sin él, «Revisar mi correo» se parte
            en tres renglones y «↻ Refrescar» deja el ícono solo arriba. */}
        <div className="flex shrink-0 items-center gap-2">
          <button
            type="button"
            onClick={() => setRevisarAbierto(true)}
            className="btn-primary min-h-[44px] flex-1 whitespace-nowrap text-sm sm:min-h-0 sm:flex-none"
          >
            Revisar mi correo
          </button>
          <button
            onClick={() => void load()}
            className="btn-ghost min-h-[44px] whitespace-nowrap text-sm sm:min-h-0"
          >
            ↻ Refrescar
          </button>
        </div>
      </div>

      <div className="card">
        {loading ? (
          <p className="text-sm text-zinc-500">Cargando...</p>
        ) : items.length === 0 ? (
          <div className="py-6 text-center">
            <p className="text-sm text-zinc-500">Bandeja vacía.</p>
            <p className="text-xs text-zinc-600 mt-1">
              Los correos a casos@arknex.net que no coinciden con un caso existente
              aparecerán aquí.
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-ink-700">
            {items.map((item) => {
              const exp = expanded[item.id];
              const isExpanded = exp !== undefined;
              const fullCorreo = exp !== "loading" ? (exp as CorreoOut | undefined) : undefined;
              return (
                <li key={item.id} className="py-3">
                  <button
                    type="button"
                    onClick={() => void toggleExpand(item)}
                    className="w-full text-left hover:bg-ink-800/50 -mx-2 px-2 py-1 rounded"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded border border-sky-500/30 bg-sky-500/10 text-sky-300">
                            ↘ IN
                          </span>
                          <span className="text-sm font-medium text-zinc-200 truncate">
                            {item.de}
                          </span>
                          {/* A quién llegó, completo y a la vista (antes solo el
                              primero; el resto vivía en un tooltip). */}
                          {item.para?.[0] && (
                            <span className="min-w-0 break-all text-[11px] text-zinc-500">
                              → {item.para.join(", ")}
                            </span>
                          )}
                          {item.adjuntos_count > 0 && (
                            <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded border border-amber-500/30 bg-amber-500/10 text-amber-300">
                              📎 {item.adjuntos_count} adjunto{item.adjuntos_count === 1 ? "" : "s"}
                            </span>
                          )}
                          {item.metadata_parseada && (
                            <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded border border-emerald-500/30 bg-emerald-500/10 text-emerald-300">
                              📋 {item.metadata_parseada._form || "form"}
                            </span>
                          )}
                          {item.triage_ia && (
                            <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded border border-violet-500/40 bg-violet-500/10 text-violet-300">
                              ✨ {decisionTexto(item.triage_ia)}
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-zinc-300 mt-0.5 truncate">{item.asunto}</p>
                        {!isExpanded && item.triage_ia?.razon && (
                          <p className="text-[11px] text-violet-300/80 mt-0.5 break-words">
                            ✨ {item.triage_ia.razon}
                          </p>
                        )}
                        {!isExpanded && (
                          <p className="text-xs text-zinc-400 mt-0.5 line-clamp-2">
                            {item.preview}
                          </p>
                        )}
                      </div>
                      <div className="text-right text-[10px] sm:text-xs text-zinc-500 flex-shrink-0">
                        <div>{new Date(item.ts).toLocaleString()}</div>
                        <div className="text-accent-400 mt-1">
                          {isExpanded ? "▲ ocultar" : "▼ ver"}
                        </div>
                      </div>
                    </div>
                  </button>

                  {verDescartados && isAdmin && (
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                      <button
                        type="button"
                        onClick={() => void handleRecuperar(item)}
                        className="btn-ghost text-xs"
                      >
                        ↩ Devolver a la bandeja
                      </button>
                      {/* El borrado de verdad vive SOLO aquí (para llegar,
                          alguien ya decidió descartarlo: dos pasos, no uno) y
                          SOLO lo ve el dueño — «la mera punta del tren», dijo
                          Joe. No basta ser admin. */}
                      {esDueno && (
                        <button
                          type="button"
                          onClick={() => setBorrarFor(item)}
                          className="btn-ghost text-xs text-red-300 hover:text-red-200"
                        >
                          🗑 Borrar definitivamente
                        </button>
                      )}
                    </div>
                  )}

                  {item.casos_imei.length > 0 && (
                    <div className="mt-2 flex flex-wrap items-center gap-1.5">
                      <span className="text-[10px] uppercase tracking-wide text-zinc-500">
                        IMEI en caso:
                      </span>
                      {item.casos_imei.map((cand) => (
                        <button
                          key={cand.caso_id}
                          type="button"
                          onClick={() => void asignarDesdeChip(item, cand)}
                          className={`text-[11px] font-mono px-2 py-0.5 rounded-full border transition max-w-full break-words text-left ${
                            cand.abierto
                              ? "border-accent-500/40 bg-accent-500/10 text-accent-300 hover:bg-accent-500/25"
                              : "border-zinc-600/50 bg-zinc-600/10 text-zinc-400 hover:bg-zinc-600/25"
                          }`}
                        >
                          {/* Cliente completo y estado del caso a la vista
                              (antes el estado vivía en un tooltip). */}
                          {cand.folio} · {cand.cliente_nombre} · {cand.estado}
                        </button>
                      ))}
                    </div>
                  )}

                  {isExpanded && (
                    <div className="mt-2 ml-2 border-l-2 border-ink-700 pl-3">
                      {exp === "loading" ? (
                        <p className="text-xs text-zinc-500 py-2">Cargando contenido...</p>
                      ) : fullCorreo ? (
                        <BandejaCorreoExpanded
                          item={item}
                          correo={fullCorreo}
                          isAdmin={isAdmin}
                          triageEnabled={triageEnabled}
                          onAsignar={() => setAsignarFor(item)}
                          onDescartar={() => setDescartarFor(item)}
                          onCrearCaso={() => {
                            setCrearConIA(false);
                            setCrearCasoFor(item);
                          }}
                          onCrearCasoIA={() => {
                            setCrearConIA(true);
                            setCrearCasoFor(item);
                          }}
                          onAsignarIA={(casoId) => void asignarIA(item, casoId)}
                          onTriage={() => void analizarConIA(item)}
                          onReprocesar={async () => {
                            try {
                              await api.post(
                                `/api/correos/${item.id}/reprocesar-metadata`,
                              );
                              toast.success("Re-procesado", "Refrescando bandeja...");
                              setExpanded((prev) => {
                                const c = { ...prev };
                                delete c[item.id];
                                return c;
                              });
                              void load();
                            } catch (err) {
                              const msg =
                                err instanceof ApiError ? err.detail : "Error";
                              toast.error("No pude re-procesar", msg);
                            }
                          }}
                        />
                      ) : null}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {asignarFor && (
        <AsignarCasoModal
          open={true}
          onOpenChange={(o) => {
            if (!o) setAsignarFor(null);
          }}
          correoId={asignarFor.id}
          correoAsunto={asignarFor.asunto}
          correoFrom={asignarFor.de}
          onAsignado={() => void load()}
        />
      )}

      <NuevoCasoModal
        open={buzonNuevo !== null}
        onClose={() => setBuzonNuevo(null)}
        prefill={buzonNuevo?.prefill}
        onCreated={async (caso: CasoDetalle) => {
          const pend = buzonNuevo;
          const ids = pend?.correoIds ?? [];
          // Se vuelven a pedir al buzón, ahora CON caso: así sus archivos
          // (adjuntos y enlaces) llegan a Evidencias. Pegarlos con el endpoint
          // de la bandeja solo movía el correo y los archivos se quedaban
          // fuera (2026-10-13).
          const pegados = new Set<number>();
          const noSeMueven = new Set<number>();
          let archivos = 0;
          if (pend?.mensajes.length) {
            try {
              const r = await api.post<ResultadoJalar>("/api/buzon/jalar", {
                uidvalidity: pend.uidvalidity,
                mensajes: pend.mensajes,
                caso_id: caso.id,
              });
              for (const j of r.jalados) {
                pegados.add(j.correo_id);
                archivos += j.adjuntos.length;
              }
              // Ya estaba aquí, o alguien lo pegó a otro caso mientras tanto:
              // no se mueve solo.
              for (const o of r.omitidos) if (o.correo_id) noSeMueven.add(o.correo_id);
            } catch {
              // Se cae al respaldo de abajo: el correo se pega sin archivos.
            }
          }
          // Respaldo: lo que ya no está en el buzón (o si falló la red) se pega
          // igual, sin archivos. El correo ya vive en la base; perderlo del
          // caso sería peor que perder sus archivos.
          const sinArchivos = ids.filter((id) => !pegados.has(id) && !noSeMueven.has(id));
          try {
            for (const id of sinArchivos) {
              await api.post(`/api/correos/${id}/asignar`, { caso_id: caso.id });
            }
            const n = pegados.size + sinArchivos.length;
            toast.success(
              "Caso abierto",
              `${caso.folio} — ${n} ${n === 1 ? "correo agregado" : "correos agregados"}` +
                (archivos
                  ? ` y ${archivos} ${archivos === 1 ? "archivo guardado" : "archivos guardados"} en Evidencias`
                  : ""),
            );
            if (pend?.mensajes.length && sinArchivos.length) {
              toast.info(
                "Sin sus archivos",
                `${sinArchivos.length === 1 ? "Un correo ya no estaba" : `${sinArchivos.length} correos ya no estaban`} en tu buzón: se agregó al caso, pero sus archivos hay que subirlos a mano.`,
              );
            }
          } catch (err) {
            const msg = err instanceof ApiError ? err.detail : "Error";
            toast.error("El caso se creó pero no pude pegarle los correos", msg);
          }
          setBuzonNuevo(null);
          void load();
        }}
      />

      <NuevoCasoModal
        open={crearCasoFor !== null}
        onClose={() => {
          setCrearCasoFor(null);
          setCrearConIA(false);
        }}
        prefill={
          crearCasoFor
            ? crearConIA && crearCasoFor.triage_ia
              ? triageToPrefill(crearCasoFor.triage_ia)
              : metadataToPrefill(crearCasoFor)
            : undefined
        }
        onCreated={async (caso: CasoDetalle) => {
          // Asignar el correo al caso recien creado y refrescar bandeja
          if (!crearCasoFor) return;
          try {
            await api.post(`/api/correos/${crearCasoFor.id}/asignar`, {
              caso_id: caso.id,
            });
            toast.success("Caso creado", `${caso.folio} — correo asignado al hilo`);
          } catch (err) {
            const msg = err instanceof ApiError ? err.detail : "Error";
            toast.error(
              "Caso creado pero NO se asignó el correo",
              `${msg} — asígnalo manual desde el caso`,
            );
          }
          setCrearCasoFor(null);
          setCrearConIA(false);
          void load();
        }}
      />

      <RevisarCorreoModal
        open={revisarAbierto}
        onOpenChange={(v) => {
          setRevisarAbierto(v);
          if (!v && searchParams.get("revisar")) {
            // Limpiar el deep-link para que recargar no vuelva a abrirlo.
            const p = new URLSearchParams(searchParams);
            p.delete("revisar");
            setSearchParams(p, { replace: true });
          }
        }}
        onJalado={() => {
          void load();
          void api
            .get<EstadoBuzon>("/api/buzon/estado")
            .then(setEstadoBuzon)
            .catch(() => undefined);
        }}
        onJaladoSinCaso={(r, mensajes) => {
          setRevisarAbierto(false);
          setBuzonNuevo({
            prefill: prefillDesdeBuzon(r.prefill),
            correoIds: r.jalados.map((j) => j.correo_id),
            mensajes,
            uidvalidity: r.uidvalidity,
          });
        }}
      />

      <ConfirmDialog
        open={descartarFor !== null}
        onOpenChange={(o) => {
          if (!o) setDescartarFor(null);
        }}
        title="Descartar correo"
        description={
          descartarFor
            ? `El correo "${descartarFor.asunto.slice(0, 60)}" se ocultará de la bandeja. Queda guardado para auditoría. Solo admin puede hacer esto.`
            : ""
        }
        confirmLabel="Descartar"
        cancelLabel="Cancelar"
        variant="danger"
        onConfirm={handleDescartar}
      />

      <ConfirmDialog
        open={borrarFor !== null}
        onOpenChange={(o) => {
          if (!o) setBorrarFor(null);
        }}
        title="Borrar definitivamente"
        description={
          borrarFor
            ? `Se borra de la base el correo "${borrarFor.asunto.slice(0, 60)}" de ${borrarFor.de}. Esto NO se deshace: no queda cuerpo, ni adjuntos, ni forma de recuperarlo. Solo queda el registro en auditoría.`
            : ""
        }
        confirmLabel="Sí, borrarlo"
        cancelLabel="Cancelar"
        variant="danger"
        onConfirm={handleBorrarDefinitivo}
      />
    </div>
  );
}

// ============================================================================
// Detalle expandido del correo (cuerpo + acciones)
// ============================================================================

function BandejaCorreoExpanded({
  item,
  correo,
  isAdmin,
  triageEnabled,
  onAsignar,
  onDescartar,
  onCrearCaso,
  onCrearCasoIA,
  onAsignarIA,
  onTriage,
  onReprocesar,
}: {
  item: BandejaItem;
  correo: CorreoOut;
  isAdmin: boolean;
  triageEnabled: boolean;
  onAsignar: () => void;
  onDescartar: () => void;
  onCrearCaso: () => void;
  onCrearCasoIA: () => void;
  onAsignarIA: (casoId: number) => void;
  onTriage: () => void;
  onReprocesar: () => void;
}) {
  const [showHtml, setShowHtml] = useState(false);
  const hasHtml = !!correo.cuerpo_html?.trim();
  const bodyText = correo.cuerpo_texto?.trim() || "(sin cuerpo de texto)";
  const t = item.triage_ia;

  const recipientsLine = useMemo(() => {
    const parts: string[] = [];
    if (correo.para.length > 0) parts.push(`Para: ${correo.para.join(", ")}`);
    if (correo.cc.length > 0) parts.push(`Cc: ${correo.cc.join(", ")}`);
    return parts.join(" · ");
  }, [correo]);

  return (
    <div className="space-y-3 py-2">
      {recipientsLine && (
        <p className="text-xs text-zinc-500">{recipientsLine}</p>
      )}

      {hasHtml ? (
        <div>
          <div className="flex items-center gap-2 text-xs text-zinc-500 mb-1">
            <button
              onClick={() => setShowHtml(false)}
              className={`px-2 py-0.5 rounded ${!showHtml ? "bg-ink-700 text-zinc-200" : "hover:bg-ink-800"}`}
            >
              Texto
            </button>
            <button
              onClick={() => setShowHtml(true)}
              className={`px-2 py-0.5 rounded ${showHtml ? "bg-ink-700 text-zinc-200" : "hover:bg-ink-800"}`}
            >
              HTML
            </button>
          </div>
          {showHtml ? (
            <EmailHtmlPreview html={correo.cuerpo_html || ""} light maxHeight="24rem" />
          ) : (
            <pre className="text-xs text-zinc-300 whitespace-pre-wrap font-mono bg-ink-950 rounded p-3 max-h-96 overflow-auto">
              {bodyText}
            </pre>
          )}
        </div>
      ) : (
        <pre className="text-xs text-zinc-300 whitespace-pre-wrap font-mono bg-ink-950 rounded p-3 max-h-96 overflow-auto">
          {bodyText}
        </pre>
      )}

      {/* Sugerencia de la IA (si ya se analizó) */}
      {t && (
        <div className="rounded-lg border border-violet-500/30 bg-violet-500/5 p-2.5 space-y-2">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[11px] font-semibold text-violet-300">
              ✨ Sugerencia de la IA
            </span>
            <span
              className={`text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded-full border ${confianzaClase(t.confianza)}`}
            >
              {decisionTexto(t)} · confianza {t.confianza}
            </span>
          </div>
          {t.razon && <p className="text-xs text-zinc-400">{t.razon}</p>}
          <div className="flex items-center gap-2 flex-wrap">
            {t.decision === "nuevo" && (
              <button
                onClick={onCrearCasoIA}
                className="btn bg-violet-600 hover:bg-violet-500 text-white text-sm"
              >
                ✨ Crear caso (IA)
              </button>
            )}
            {t.decision === "vincular" && t.caso_id !== null && (
              <button
                onClick={() => onAsignarIA(t.caso_id as number)}
                className="btn-primary text-sm"
              >
                ✨ Asignar a {t.caso_te}
              </button>
            )}
            {triageEnabled && (
              <button
                onClick={onTriage}
                className="btn-ghost text-xs text-violet-300 hover:text-violet-200"
              >
                ↻ Revisar otra vez
              </button>
            )}
          </div>
        </div>
      )}
      {!t && triageEnabled && (
        <button
          onClick={onTriage}
          className="btn-ghost text-sm text-violet-300 hover:text-violet-200 self-start"
        >
          ✨ Revisar con IA
        </button>
      )}

      <div className="flex items-center gap-2 pt-2 border-t border-ink-700 flex-wrap">
        {item.metadata_parseada && (
          <button
            onClick={onCrearCaso}
            className="btn bg-emerald-600 hover:bg-emerald-500 text-white text-sm"
          >
            📋 Crear caso desde este correo
          </button>
        )}
        {!item.metadata_parseada && (
          <button
            onClick={onReprocesar}
            className="btn-ghost text-sm text-zinc-400 hover:text-zinc-200"
          >
            ↻ Re-detectar formato
          </button>
        )}
        <button onClick={onAsignar} className="btn-primary text-sm">
          Asignar a caso
        </button>
        {isAdmin && (
          <button
            onClick={onDescartar}
            className="btn-ghost text-sm text-red-400 hover:text-red-300"
          >
            Descartar
          </button>
        )}
        <span className="ml-auto min-w-0 max-w-full break-all text-[10px] text-zinc-600">
          ID #{item.id}
          {correo.message_id && <> · Id del mensaje: {mostrarMsgId(correo.message_id)}</>}
        </span>
      </div>
    </div>
  );
}
